import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const money  = (n) => `${Number(n||0).toLocaleString('en-US', { maximumFractionDigits:0 })} RWF`
const num    = (n) => Number(n||0).toLocaleString()
const fmtTime = (d) => {
  if (!d) return '—'
  const now  = new Date()
  const date = new Date(d)
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60)    return 'Just now'
  if (diff < 3600)  return `${Math.floor(diff/60)}m ago`
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`
  return date.toLocaleDateString('en-GB', { day:'2-digit', month:'short' })
}

// Greeting based on time
const greeting = () => {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

// Sparkline SVG
function Spark({ data = [], color = '#e8440a' }) {
  if (data.length < 2) return null
  const max = Math.max(...data, 1)
  const min = Math.min(...data)
  const r   = max - min || 1
  const W = 80, H = 28
  const pts = data.map((v,i) => `${(i/(data.length-1))*W},${H-((v-min)/r)*(H-4)-2}`).join(' ')
  return (
    <svg width={W} height={H} style={{ overflow:'visible', opacity:0.8 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, delta, icon, color, spark, onClick }) {
  const up = delta > 0
  return (
    <div onClick={onClick}
      style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, padding:'18px 20px', flex:1, minWidth:140, cursor:onClick?'pointer':'default', position:'relative', overflow:'hidden', transition:'box-shadow 0.15s, transform 0.15s' }}
      onMouseEnter={e => { if(onClick){e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform='translateY(-2px)'}}}
      onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none'}}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:color, borderRadius:'12px 12px 0 0' }} />
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
        <div style={{ fontSize:10, fontWeight:600, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace" }}>{label}</div>
        <span style={{ fontSize:15, opacity:0.5 }}>{icon}</span>
      </div>
      <div style={{ fontSize:26, fontWeight:800, letterSpacing:'-0.6px', color:'#111317', lineHeight:1, marginBottom:8 }}>{value}</div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
          {sub}
          {delta != null && (
            <span style={{ marginLeft:6, color:up?'#15803d':'#b91c1c', fontWeight:700 }}>
              {up?'↑':'↓'}{Math.abs(delta)}%
            </span>
          )}
        </div>
        {spark && <Spark data={spark} color={color} />}
      </div>
    </div>
  )
}

// ── Activity feed item ────────────────────────────────────────────────────────
const ACTIVITY_CONFIG = {
  order:   { icon:'🛍', color:'#1d4ed8', bg:'#eff6ff'  },
  payment: { icon:'💳', color:'#15803d', bg:'#f0fdf4'  },
  user:    { icon:'👤', color:'#0369a1', bg:'#f0f9ff'  },
  shop:    { icon:'🏪', color:'#7c3aed', bg:'#f5f3ff'  },
  clip:    { icon:'▶',  color:'#b45309', bg:'#fffbeb'  },
  ticket:  { icon:'🎫', color:'#b91c1c', bg:'#fef2f2'  },
  driver:  { icon:'🚚', color:'#15803d', bg:'#f0fdf4'  },
  live:    { icon:'🔴', color:'#ef4444', bg:'#fef2f2'  },
}
const ac = (type) => ACTIVITY_CONFIG[type] || { icon:'📣', color:'#6b7280', bg:'#f3f4f6' }

function ActivityItem({ item, navigate }) {
  const cfg = ac(item.type)
  return (
    <div
      onClick={() => item.link && navigate(item.link)}
      style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 0', borderBottom:'1px solid #f3f3f0', cursor:item.link?'pointer':'default' }}
      onMouseEnter={e => { if(item.link) e.currentTarget.style.background='#fafaf8' }}
      onMouseLeave={e => { e.currentTarget.style.background='transparent' }}>
      <div style={{ width:32, height:32, borderRadius:8, background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
        {cfg.icon}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:600, color:'#111317', lineHeight:1.4 }}>{item.title}</div>
        <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{item.sub}</div>
      </div>
      <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", whiteSpace:'nowrap', flexShrink:0 }}>{fmtTime(item.time)}</div>
    </div>
  )
}

// ── Quick action card ─────────────────────────────────────────────────────────
function QuickAction({ icon, label, sub, color, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'14px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:12, transition:'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor=color }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor='#e9e9e4' }}>
      <div style={{ width:36, height:36, borderRadius:9, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:12, fontWeight:800, color:'#111317' }}>{label}</div>
        <div style={{ fontSize:11, color:'#9a9a94', marginTop:1 }}>{sub}</div>
      </div>
    </div>
  )
}

// ── Pending alert ─────────────────────────────────────────────────────────────
function PendingAlert({ icon, count, label, color, onClick }) {
  if (!count) return null
  return (
    <div onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:`${color}0d`, border:`1px solid ${color}30`, borderRadius:8, cursor:'pointer', transition:'opacity 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.opacity='0.8'}
      onMouseLeave={e => e.currentTarget.style.opacity='1'}>
      <span style={{ fontSize:16 }}>{icon}</span>
      <div style={{ flex:1 }}>
        <span style={{ fontSize:13, fontWeight:700, color:'#111317' }}>{count} </span>
        <span style={{ fontSize:13, color:'#374151' }}>{label}</span>
      </div>
      <span style={{ fontSize:12, color:color, fontWeight:700 }}>View →</span>
    </div>
  )
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate()
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [time,    setTime]    = useState(new Date())

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000)
    return () => clearInterval(t)
  }, [])

  const fetchDashboard = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/analytics/admin/dashboard')
      setData(res.data)
    } catch {
      // Mock data
      const genSpark = (base) => Array.from({ length: 14 }, (_, i) => Math.max(0, base + Math.sin(i/2)*base*0.4 + (Math.random()-0.5)*base*0.3))
      setData({
        admin: { name: 'Admin', role: 'superadmin' },
        kpis: {
          today_revenue:    { value: 480000,  delta: 12.4, spark: genSpark(16000) },
          today_orders:     { value: 38,       delta: 8.2,  spark: genSpark(38)    },
          active_users:     { value: 1240,     delta: 5.1,  spark: genSpark(1240)  },
          open_tickets:     { value: 14,       delta: -3.2, spark: genSpark(14)    },
        },
        totals: {
          total_users:    8420,
          total_shops:    312,
          total_reels:    2840,
          total_revenue:  48200000,
        },
        pending: {
          orders:   42,
          sellers:  8,
          tickets:  14,
          payments: 6,
        },
        activity: [
          { type:'order',   title:'New order #1089 placed',          sub:'Alice M. · 32,000 RWF',            time: new Date(Date.now()-180000).toISOString(),   link:'/orders' },
          { type:'user',    title:'New seller registration',          sub:'Boutique Kigali applied for shop', time: new Date(Date.now()-720000).toISOString(),   link:'/sellers' },
          { type:'payment', title:'Payment confirmed — Order #1087',  sub:'MTN MoMo · 18,500 RWF',           time: new Date(Date.now()-1800000).toISOString(),  link:'/payments' },
          { type:'ticket',  title:'Support ticket #48 opened',        sub:'Driver cannot login to app',       time: new Date(Date.now()-3600000).toISOString(),  link:'/helpcenter' },
          { type:'clip',    title:'New clip posted by Fashion House',  sub:'Summer Collection 2025',          time: new Date(Date.now()-5400000).toISOString(),  link:'/reels' },
          { type:'live',    title:'Live stream started',               sub:'Tech Corner · Gadget Launch',     time: new Date(Date.now()-7200000).toISOString(),  link:'/livestreams' },
          { type:'order',   title:'Order #1085 delivered',             sub:'Bob K. · Kigali Central',         time: new Date(Date.now()-10800000).toISOString(), link:'/orders' },
          { type:'driver',  title:'New driver registered',             sub:'Jean P. · Motorcycle',            time: new Date(Date.now()-14400000).toISOString(), link:'/drivers' },
        ],
        top_shops_today: [
          { name:'Fashion House',  revenue: 124000, orders: 18 },
          { name:'Tech Corner',    revenue:  98000, orders: 12 },
          { name:'Beauty World',   revenue:  76000, orders: 22 },
          { name:'Sports Arena',   revenue:  64000, orders: 9  },
        ],
        live_now: [
          { shop: 'Fashion House', viewers: 84,  duration: '23m' },
          { shop: 'Food & Drinks', viewers: 42,  duration: '8m'  },
        ],
      })
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchDashboard() }, [fetchDashboard])

  const d = data || {}
  const k = d.kpis || {}
  const p = d.pending || {}

  const dayOfWeek = time.toLocaleDateString('en-GB', { weekday:'long' })
  const dateStr   = time.toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })
  const timeStr   = time.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })

  return (
    <div style={{ padding:'28px 32px 80px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        .fade-up{animation:fadeUp 0.4s ease both}
        .fade-up:nth-child(1){animation-delay:0.05s}
        .fade-up:nth-child(2){animation-delay:0.1s}
        .fade-up:nth-child(3){animation-delay:0.15s}
        .fade-up:nth-child(4){animation-delay:0.2s}
        .fade-up:nth-child(5){animation-delay:0.25s}
        .fade-up:nth-child(6){animation-delay:0.3s}
      `}</style>

      {/* ── Header ── */}
      <div className="fade-up" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28 }}>
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.12em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }}>
            {dayOfWeek}, {dateStr} · {timeStr}
          </div>
          <div style={{ fontSize:24, fontWeight:800, letterSpacing:'-0.5px', color:'#111317' }}>
            {greeting()}, {d.admin?.name || 'Admin'} 👋
          </div>
          <div style={{ fontSize:13, color:'#9a9a94', marginTop:4 }}>
            Here's what's happening on Kudos Live Market today.
          </div>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          {/* Live now indicator */}
          {d.live_now?.length > 0 && (
            <div onClick={() => navigate('/livestreams')}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, cursor:'pointer' }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:'#ef4444', animation:'livePulse 1.2s infinite', display:'inline-block' }} />
              <style>{`@keyframes livePulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(1.3)}}`}</style>
              <span style={{ fontSize:12, fontWeight:700, color:'#b91c1c' }}>{d.live_now.length} Live Now</span>
            </div>
          )}
          <button onClick={fetchDashboard}
            style={{ height:42, width:42, borderRadius:10, border:'1px solid #e9e9e4', background:'#fff', cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>
            ↻
          </button>
        </div>
      </div>

      {/* ── Pending alerts ── */}
      {(p.orders > 0 || p.sellers > 0 || p.tickets > 0 || p.payments > 0) && (
        <div className="fade-up" style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
          <PendingAlert icon="🛍"  count={p.orders}   label="orders awaiting confirmation" color="#1d4ed8" onClick={() => navigate('/orders')} />
          <PendingAlert icon="🏪"  count={p.sellers}  label="seller registrations pending" color="#7c3aed" onClick={() => navigate('/sellers')} />
          <PendingAlert icon="🎫"  count={p.tickets}  label="open support tickets"         color="#b91c1c" onClick={() => navigate('/helpcenter')} />
          <PendingAlert icon="💳"  count={p.payments} label="payments pending review"       color="#b45309" onClick={() => navigate('/payments')} />
        </div>
      )}

      {/* ── Today's KPIs ── */}
      <div className="fade-up" style={{ marginBottom:8 }}>
        <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono',monospace", marginBottom:12 }}>Today</div>
      </div>
      <div className="fade-up" style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
        <KpiCard label="Revenue Today"  value={money(k.today_revenue?.value)}  sub="vs. yesterday" delta={k.today_revenue?.delta}  spark={k.today_revenue?.spark}  color="#e8440a" icon="💰" onClick={() => navigate('/payments')} />
        <KpiCard label="Orders Today"   value={num(k.today_orders?.value)}      sub="vs. yesterday" delta={k.today_orders?.delta}   spark={k.today_orders?.spark}   color="#1d4ed8" icon="🛍" onClick={() => navigate('/orders')} />
        <KpiCard label="Active Users"   value={num(k.active_users?.value)}      sub="last 24h"      delta={k.active_users?.delta}   spark={k.active_users?.spark}   color="#15803d" icon="👥" onClick={() => navigate('/users')} />
        <KpiCard label="Open Tickets"   value={num(k.open_tickets?.value)}      sub="need response" delta={k.open_tickets?.delta}   spark={k.open_tickets?.spark}   color="#b91c1c" icon="🎫" onClick={() => navigate('/helpcenter')} />
      </div>

      {/* ── All-time totals ── */}
      <div className="fade-up" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12, marginBottom:24 }}>
        {[
          { label:'Total Users',   value: num(d.totals?.total_users),   icon:'👥', color:'#0369a1', link:'/users' },
          { label:'Active Shops',  value: num(d.totals?.total_shops),   icon:'🏪', color:'#7c3aed', link:'/sellers' },
          { label:'Clips Posted',  value: num(d.totals?.total_reels),   icon:'▶',  color:'#b45309', link:'/reels' },
          { label:'Videos Live',   value: num(d.totals?.total_videos),  icon:'🎬', color:'#0d9488', link:'/videos' },
          { label:'Total Revenue', value: money(d.totals?.total_revenue),icon:'💰', color:'#e8440a', link:'/payments' },
        ].map(({ label, value, icon, color, link }) => (
          <div key={label} onClick={() => navigate(link)}
            style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'14px 16px', cursor:'pointer', transition:'all 0.15s', display:'flex', alignItems:'center', gap:12 }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 14px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor=color }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor='#e9e9e4' }}>
            <div style={{ width:36, height:36, borderRadius:9, background:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{icon}</div>
            <div>
              <div style={{ fontSize:10, fontWeight:600, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace" }}>{label}</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#111317', letterSpacing:'-0.3px' }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Main content grid ── */}
      <div className="fade-up" style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20, marginBottom:20 }}>

        {/* Activity Feed */}
        <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, overflow:'hidden' }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #e9e9e4', background:'#fafaf8', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:13, fontWeight:800 }}>Recent Activity</div>
            <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>Live feed</div>
          </div>
          <div style={{ padding:'4px 20px' }}>
            {loading ? (
              <div style={{ padding:'32px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>Loading…</div>
            ) : (
              (d.activity || []).map((item, i) => (
                <ActivityItem key={i} item={item} navigate={navigate} />
              ))
            )}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Live Streams */}
          {d.live_now?.length > 0 && (
            <div style={{ background:'#111317', border:'1px solid #1f1f1f', borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #1f1f1f', display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:7, height:7, borderRadius:'50%', background:'#ef4444', display:'inline-block', animation:'livePulse 1.2s infinite' }} />
                <div style={{ fontSize:12, fontWeight:800, color:'#fff' }}>Live Now</div>
              </div>
              {d.live_now.map((stream, i) => (
                <div key={i} onClick={() => navigate('/livestreams')} style={{ padding:'12px 16px', borderBottom:i<d.live_now.length-1?'1px solid #1f1f1f':'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between' }}
                  onMouseEnter={e => e.currentTarget.style.background='#1a1a1a'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#fff' }}>{stream.shop}</div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', fontFamily:"'JetBrains Mono',monospace" }}>{stream.duration}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#ef4444', fontFamily:"'JetBrains Mono',monospace" }}>{stream.viewers}</div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)' }}>viewers</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Top Shops Today */}
          <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e9e9e4', background:'#fafaf8', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:12, fontWeight:800 }}>Top Shops Today</div>
              <span onClick={() => navigate('/sellers')} style={{ fontSize:11, color:'#e8440a', fontWeight:700, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace" }}>All →</span>
            </div>
            <div style={{ padding:'4px 16px' }}>
              {(d.top_shops_today || []).map((shop, i) => {
                const max = Math.max(...(d.top_shops_today||[]).map(s=>s.revenue), 1)
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:i<(d.top_shops_today?.length-1)?'1px solid #f3f3f0':'none' }}>
                    <div style={{ width:22, height:22, borderRadius:5, background:i===0?'#111317':'#f3f3f0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:i===0?'#fff':'#9a9a94', flexShrink:0 }}>{i+1}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{shop.name}</div>
                      <div style={{ width:'100%', height:3, background:'#f3f3f0', borderRadius:2, marginTop:4 }}>
                        <div style={{ width:`${(shop.revenue/max)*100}%`, height:'100%', background:'#e8440a', borderRadius:2 }} />
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:11, fontWeight:800, fontFamily:"'JetBrains Mono',monospace" }}>{(shop.revenue/1000).toFixed(0)}K</div>
                      <div style={{ fontSize:10, color:'#9a9a94' }}>{shop.orders} orders</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e9e9e4', background:'#fafaf8' }}>
              <div style={{ fontSize:12, fontWeight:800 }}>Quick Actions</div>
            </div>
            <div style={{ padding:12, display:'flex', flexDirection:'column', gap:8 }}>
              <QuickAction icon="🔔" label="Send Notification"    sub="Broadcast to users"    color="#e8440a" onClick={() => navigate('/notifications')} />
              <QuickAction icon="📊" label="View Full Analytics"  sub="Detailed reports"      color="#1d4ed8" onClick={() => navigate('/analytics')} />
              <QuickAction icon="📥" label="Payout Report"        sub="Export for bank"       color="#15803d" onClick={() => navigate('/payments')} />
              <QuickAction icon="⚙" label="App Configuration"    sub="Fees, categories"      color="#7c3aed" onClick={() => navigate('/settings')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}